import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import { 
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  X,
  Eye,
  Check,
  AlertTriangle,
  Info,
  CheckCircle,
  Wrench,
  Calendar,
  Users,
  BarChart3,
  RefreshCw,
  AlertCircle
} from 'lucide-react'

const TYPE_CONFIG = {
  info: { label: 'Info', color: 'blue', icon: Info, bgClass: 'bg-blue-50 border-blue-200' },
  success: { label: 'Successo', color: 'green', icon: CheckCircle, bgClass: 'bg-green-50 border-green-200' },
  warning: { label: 'Avviso', color: 'yellow', icon: AlertTriangle, bgClass: 'bg-yellow-50 border-yellow-200' },
  critical: { label: 'Critico', color: 'red', icon: AlertTriangle, bgClass: 'bg-red-50 border-red-200' }
}

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'Tutti gli utenti' },
  { value: 'responsabili', label: 'Solo Responsabili' },
  { value: 'coordinatori', label: 'Solo Coordinatori' },
  { value: 'educatori', label: 'Solo Educatori' },
  { value: 'trial', label: 'Utenti in Trial' },
  { value: 'paying', label: 'Utenti Paganti' }
]

// Helper per estrarre array da risposta API (gestisce strutture diverse)
const extractAnnouncements = (response) => {
  const data = response?.data
  console.log('Raw announcements response:', data)
  
  // Prova diverse strutture possibili
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.data?.announcements)) return data.data.announcements
  if (Array.isArray(data?.announcements)) return data.announcements
  
  console.warn('Struttura risposta annunci non riconosciuta:', data)
  return []
}

// Preview Banner Component
const BannerPreview = ({ announcement }) => {
  const config = TYPE_CONFIG[announcement.type] || TYPE_CONFIG.info
  const Icon = config.icon

  return (
    <div className={`border rounded-lg p-4 ${config.bgClass}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 text-${config.color}-600 flex-shrink-0 mt-0.5`} />
        <div className="flex-1">
          <p className="font-medium text-gray-900">{announcement.title || 'Titolo annuncio'}</p>
          <p className="text-sm text-gray-600 mt-1">{announcement.message || 'Messaggio annuncio...'}</p>
          {announcement.linkUrl && (
            <a href="#" className={`text-sm text-${config.color}-600 hover:underline mt-2 inline-block`}>
              {announcement.linkText || 'Scopri di più'} →
            </a>
          )}
        </div>
        {announcement.dismissible && (
          <button className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  )
}

// Create/Edit Modal
const AnnouncementModal = ({ announcement, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: announcement?.title || '',
    message: announcement?.message || '',
    linkUrl: announcement?.linkUrl || '',
    linkText: announcement?.linkText || '',
    type: announcement?.type || 'info',
    audience: announcement?.audience || 'all',
    isPublic: announcement?.isPublic || false,
    startDate: announcement?.startDate ? announcement.startDate.slice(0, 16) : '',
    endDate: announcement?.endDate ? announcement.endDate.slice(0, 16) : '',
    dismissible: announcement?.dismissible !== false,
    dismissDurationHours: announcement?.dismissDurationHours || 24,
    showOnAllPages: announcement?.showOnAllPages || false,
    position: announcement?.position || 'top',
    isActive: announcement?.isActive !== false
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  const validate = () => {
    const newErrors = {}
    const titleLength = formData.title.trim().length
    const messageLength = formData.message.trim().length
    
    if (!titleLength) {
      newErrors.title = 'Titolo richiesto'
    } else if (titleLength < 2 || titleLength > 100) {
      newErrors.title = 'Titolo: 2-100 caratteri'
    }
    
    if (!messageLength) {
      newErrors.message = 'Messaggio richiesto'
    } else if (messageLength < 5 || messageLength > 500) {
      newErrors.message = 'Messaggio: 5-500 caratteri'
    }
    
    if (formData.linkUrl && !formData.linkUrl.startsWith('http')) {
      newErrors.linkUrl = 'URL deve iniziare con http:// o https://'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setSaving(true)
    setErrors({})
    
    try {
      // Prepara i dati - converti le date in UTC per MongoDB
      const dataToSend = {
        ...formData,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined
      }
      
      // Rimuovi campi vuoti
      if (!dataToSend.linkUrl) delete dataToSend.linkUrl
      if (!dataToSend.linkText) delete dataToSend.linkText
      if (!dataToSend.startDate) delete dataToSend.startDate
      if (!dataToSend.endDate) delete dataToSend.endDate
      
      await onSave(dataToSend)
      onClose()
    } catch (error) {
      console.error('Errore salvataggio:', error)
      // Mostra errori dal backend
      if (error.response?.data?.errors) {
        const backendErrors = {}
        error.response.data.errors.forEach(err => {
          backendErrors[err.path] = err.msg
        })
        setErrors(backendErrors)
      } else {
        setErrors({ general: error.response?.data?.message || 'Errore nel salvataggio' })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {announcement ? 'Modifica Annuncio' : 'Nuovo Annuncio'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Errore generale */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              {errors.general}
            </div>
          )}

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Anteprima
            </label>
            <BannerPreview announcement={formData} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titolo *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 ${errors.title ? 'border-red-500' : ''}`}
                  placeholder="es. Nuova funzionalità disponibile!"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Messaggio *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                  className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 ${errors.message ? 'border-red-500' : ''}`}
                  placeholder="Descrivi l'annuncio..."
                />
                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL Link (opzionale)
                  </label>
                  <input
                    type="text"
                    value={formData.linkUrl}
                    onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 ${errors.linkUrl ? 'border-red-500' : ''}`}
                    placeholder="https://..."
                  />
                  {errors.linkUrl && <p className="text-red-500 text-xs mt-1">{errors.linkUrl}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Testo Link
                  </label>
                  <input
                    type="text"
                    value={formData.linkText}
                    onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                    placeholder="Scopri di più"
                  />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                  >
                    {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destinatari
                  </label>
                  <select
                    value={formData.audience}
                    onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                  >
                    {AUDIENCE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Inizio
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Fine
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.dismissible}
                    onChange={(e) => setFormData({ ...formData, dismissible: e.target.checked })}
                    className="rounded text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Può essere chiuso dall'utente</span>
                </label>

                {formData.dismissible && (
                  <div className="ml-6">
                    <label className="block text-xs text-gray-500 mb-1">
                      Nascondi per (ore)
                    </label>
                    <input
                      type="number"
                      value={formData.dismissDurationHours}
                      onChange={(e) => setFormData({ ...formData, dismissDurationHours: parseInt(e.target.value) })}
                      min={1}
                      max={720}
                      className="w-24 border rounded px-2 py-1 text-sm"
                    />
                  </div>
                )}

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.showOnAllPages}
                    onChange={(e) => setFormData({ ...formData, showOnAllPages: e.target.checked })}
                    className="rounded text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Mostra su tutte le pagine</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Attivo</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                    className="rounded text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">
                    Visibile senza login
                    <span className="text-xs text-gray-400 ml-1">(pagina login, landing)</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Salvataggio...' : (announcement ? 'Aggiorna' : 'Crea')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Maintenance Modal
const MaintenanceModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    startDateTime: '',
    endDateTime: '',
    message: '',
    showCountdown: true
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.startDateTime || !formData.endDateTime) return
    
    // Validazione messaggio (minimo 5 caratteri)
    if (formData.message.trim().length < 5) {
      setError('Il messaggio deve avere almeno 5 caratteri')
      return
    }

    setSaving(true)
    setError('')
    try {
      // Converti le date in UTC per MongoDB
      const dataToSend = {
        message: formData.message,
        startDate: new Date(formData.startDateTime).toISOString(),
        endDate: new Date(formData.endDateTime).toISOString()
      }
      await onCreate(dataToSend)
      onClose()
    } catch (err) {
      console.error('Errore creazione avviso:', err)
      setError(err.response?.data?.message || 'Errore nella creazione dell\'avviso')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="w-5 h-5 text-yellow-600" />
            Avviso Manutenzione
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inizio Manutenzione *
              </label>
              <input
                type="datetime-local"
                value={formData.startDateTime}
                onChange={(e) => setFormData({ ...formData, startDateTime: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fine Prevista *
              </label>
              <input
                type="datetime-local"
                value={formData.endDateTime}
                onChange={(e) => setFormData({ ...formData, endDateTime: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Messaggio (opzionale)
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={2}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500"
              placeholder="es. Aggiornamento sistema per miglioramenti delle prestazioni"
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.showCountdown}
              onChange={(e) => setFormData({ ...formData, showCountdown: e.target.checked })}
              className="rounded text-primary-500 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Mostra countdown</span>
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {saving ? 'Creazione...' : 'Crea Avviso'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Main Page
export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState(null)
  const [includeExpired, setIncludeExpired] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    fetchAnnouncements()
  }, [includeExpired])

  const fetchAnnouncements = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await adminApi.getAnnouncements({ includeExpired })
      const data = extractAnnouncements(response)
      setAnnouncements(data)
    } catch (err) {
      console.error('Errore caricamento annunci:', err)
      setError(err.message || 'Errore nel caricamento degli annunci')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (data) => {
    if (editingAnnouncement) {
      await adminApi.updateAnnouncement(editingAnnouncement._id, data)
    } else {
      await adminApi.createAnnouncement(data)
    }
    setSuccessMessage(editingAnnouncement ? 'Annuncio aggiornato!' : 'Annuncio creato!')
    setTimeout(() => setSuccessMessage(''), 3000)
    fetchAnnouncements()
  }

  const handleDelete = async (id) => {
    if (!confirm('Eliminare questo annuncio?')) return
    try {
      await adminApi.deleteAnnouncement(id)
      fetchAnnouncements()
    } catch (err) {
      console.error('Errore eliminazione:', err)
    }
  }

  const handleToggle = async (id) => {
    try {
      await adminApi.toggleAnnouncement(id)
      fetchAnnouncements()
    } catch (err) {
      console.error('Errore toggle:', err)
    }
  }

  const handleCreateMaintenance = async (data) => {
    await adminApi.createMaintenanceNotice(data)
    setSuccessMessage('Avviso manutenzione creato!')
    setTimeout(() => setSuccessMessage(''), 3000)
    fetchAnnouncements()
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Annunci & Banner</h1>
          <p className="text-gray-500 mt-1">Gestisci comunicazioni globali per gli utenti</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore nel caricamento</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchAnnouncements}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <RefreshCw className="w-4 h-4" />
            Riprova
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Annunci & Banner</h1>
          <p className="text-gray-500 mt-1">Gestisci comunicazioni globali per gli utenti</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowMaintenanceModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-yellow-500 text-yellow-700 rounded-lg hover:bg-yellow-50"
          >
            <Wrench className="w-5 h-5" />
            Manutenzione
          </button>
          <button
            onClick={() => { setEditingAnnouncement(null); setShowModal(true) }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5" />
            Nuovo Annuncio
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => setIncludeExpired(e.target.checked)}
            className="rounded text-primary-500 focus:ring-primary-500"
          />
          <span className="text-sm text-gray-700">Mostra scaduti</span>
        </label>
      </div>

      {/* Announcements List */}
      <div className="bg-white rounded-xl shadow-sm border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : announcements.length === 0 ? (
          <div className="text-center py-12">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nessun annuncio</p>
            <button
              onClick={() => { setEditingAnnouncement(null); setShowModal(true) }}
              className="mt-3 text-primary-600 hover:text-primary-700 text-sm"
            >
              Crea il primo annuncio →
            </button>
          </div>
        ) : (
          <div className="divide-y">
            {announcements.map((announcement) => {
              const config = TYPE_CONFIG[announcement.type] || TYPE_CONFIG.info
              const Icon = config.icon
              const isExpired = announcement.endDate && new Date(announcement.endDate) < new Date()

              return (
                <div key={announcement._id} className={`p-4 hover:bg-gray-50 ${isExpired ? 'opacity-60' : ''}`}>
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg bg-${config.color}-100`}>
                      <Icon className={`w-5 h-5 text-${config.color}-600`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900">{announcement.title}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          announcement.isActive && !isExpired
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {isExpired ? 'Scaduto' : (announcement.isActive ? 'Attivo' : 'Inattivo')}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded-full bg-${config.color}-100 text-${config.color}-700`}>
                          {config.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{announcement.message}</p>
                      
                      <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {AUDIENCE_OPTIONS.find(a => a.value === announcement.audience)?.label}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(announcement.startDate)} - {formatDate(announcement.endDate)}
                        </span>
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" />
                          {announcement.stats?.views || 0} views · {announcement.stats?.clicks || 0} click · {announcement.stats?.dismisses || 0} chiusure
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggle(announcement._id)}
                        className={`p-2 rounded-lg ${
                          announcement.isActive
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={announcement.isActive ? 'Disattiva' : 'Attiva'}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => { setEditingAnnouncement(announcement); setShowModal(true) }}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        title="Modifica"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(announcement._id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Elimina"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && (
        <AnnouncementModal
          announcement={editingAnnouncement}
          onClose={() => { setShowModal(false); setEditingAnnouncement(null) }}
          onSave={handleSave}
        />
      )}

      {showMaintenanceModal && (
        <MaintenanceModal
          onClose={() => setShowMaintenanceModal(false)}
          onCreate={handleCreateMaintenance}
        />
      )}
    </div>
  )
}
